import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../lib/api';
import { microsoftLogoutIfNeeded } from '../lib/microsoftAuth';
import { clearStoredSession, getStoredAuthProvider, getStoredToken, persistSession } from '../lib/authStorage';

const AuthContext = createContext();
const AUTH_REQUEST_TIMEOUT_MS = 20000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(getStoredToken());
  const [authProvider, setAuthProvider] = useState(getStoredAuthProvider() || 'local');

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { timeout: AUTH_REQUEST_TIMEOUT_MS });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { timeout: AUTH_REQUEST_TIMEOUT_MS });
    const { access_token, user: userData } = response.data;
    persistSession(access_token, 'local');
    setToken(access_token);
    setAuthProvider('local');
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    return userData;
  };

  const loginWithMicrosoftToken = async (idToken) => {
    const response = await axios.post(`${API}/auth/microsoft`, { id_token: idToken }, { timeout: AUTH_REQUEST_TIMEOUT_MS });
    const { access_token, user: userData } = response.data;
    persistSession(access_token, 'microsoft');
    setToken(access_token);
    setAuthProvider('microsoft');
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    return userData;
  };

  const register = async (name, email, password, role = 'tratador') => {
    await axios.post(`${API}/auth/register`, { name, email, password, role });
  };

  const logout = () => {
    clearStoredSession();
    setToken(null);
    setAuthProvider('local');
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    if (authProvider === 'microsoft') {
      // Keep the local logout immediate and clear Microsoft session in the background.
      microsoftLogoutIfNeeded().catch((error) => {
        console.error('Error logging out from Microsoft:', error);
      });
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithMicrosoftToken, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
